import { memo, type CSSProperties, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';

/**
 * Markdown rendering for every surface that shows prose.
 *
 * Replaces a hand-rolled regex renderer, for two reasons. It had no table support — GFM pipe
 * tables came out as literal `|` text in a paragraph. And it interpolated content straight into
 * an HTML string fed to `dangerouslySetInnerHTML`, so any raw HTML in a document survived into the
 * page: an ingested file containing `<img src=x onerror=…>` would have executed in a reviewer's
 * authenticated session. KB bodies are written from Slack messages, uploads and Granola
 * notes, so that content is not ours to trust.
 *
 * react-markdown renders React elements and never assembles HTML, so raw HTML in the source is
 * escaped and shown as text unless `rehype-raw` is deliberately added — which it is not.
 *
 * Element styling lives in `components` rather than in the markdown string, which is also what
 * lets CitedMarkdown inject citation markers declaratively instead of walking the DOM.
 */

/**
 * The four block renderers CitedMarkdown wraps to inject citation markers.
 *
 * Exported as named components rather than read back out of the `Components` map below: indexing
 * that map yields a union across every tag it can hold, so `markdownComponents.p` types as
 * something that could be an SVG element and won't accept paragraph props.
 */
export const MdParagraph = ({ children }: { children?: ReactNode }) => (
  <p className="text-sm text-ink-secondary leading-relaxed mb-3">{children}</p>
);
export const MdListItem = ({ children }: { children?: ReactNode }) => (
  <li className="text-sm text-ink-secondary leading-relaxed">{children}</li>
);
export const MdTableCell = ({ children, style }: { children?: ReactNode; style?: CSSProperties }) => (
  <td style={style} className="px-3 py-2 text-xs text-ink-secondary align-top">
    {children}
  </td>
);
export const MdBlockquote = ({ children }: { children?: ReactNode }) => (
  <blockquote className="border-l-2 border-border-strong pl-3 my-3 text-sm text-ink-muted italic">
    {children}
  </blockquote>
);

/** Tailwind classes per element, shared with CitedMarkdown so both render identically. */
export const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-lg font-bold text-ink mt-6 mb-3 first:mt-0">{children}</h1>,
  h2: ({ children }) => (
    <h2 className="text-base font-bold text-ink mt-6 mb-2 pb-1 border-b border-border-subtle first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => <h3 className="text-sm font-semibold text-ink mt-5 mb-1.5 first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-ink mt-4 mb-1 first:mt-0">{children}</h4>,
  h5: ({ children }) => <h5 className="text-xs font-semibold text-ink mt-3 mb-1 first:mt-0">{children}</h5>,
  h6: ({ children }) => <h6 className="text-xs font-semibold text-ink-muted mt-3 mb-1 first:mt-0">{children}</h6>,

  p: ({ children }) => <MdParagraph>{children}</MdParagraph>,
  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="line-through text-ink-faint">{children}</del>,

  ul: ({ children }) => <ul className="list-disc ml-5 mb-3 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-5 mb-3 space-y-0.5">{children}</ol>,
  li: ({ children }) => <MdListItem>{children}</MdListItem>,

  blockquote: ({ children }) => <MdBlockquote>{children}</MdBlockquote>,

  hr: () => <hr className="my-4 border-border-subtle" />,

  // Links from ingested content point outward, so they open in a new tab and carry noreferrer.
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-orange-600 dark:text-orange-400 hover:underline break-words"
    >
      {children}
    </a>
  ),

  // react-markdown gives `inline` for backtick spans; fenced blocks get their own <pre>.
  code: ({ className, children, ...props }) => {
    const isBlock = 'inline' in props ? !props.inline : /language-/.test(className ?? '');
    if (isBlock) {
      return (
        <code className="block text-[11px] font-mono text-ink-secondary whitespace-pre">{children}</code>
      );
    }
    return (
      <code className="text-[11px] bg-surface-muted px-1 py-0.5 rounded font-mono text-orange-700 dark:text-orange-400 break-words">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-surface-subtle border border-border-subtle rounded-md p-3 mb-3 overflow-x-auto">
      {children}
    </pre>
  ),

  // ─── Tables (GFM) ──────────────────────────────────────────────────────────
  // Wrapped in its own horizontal scroll container: these render inside panes that can be
  // dragged down to ~300px, and without it a wide table either overflows the layout or forces
  // the whole page to scroll sideways.
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-surface-subtle">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-border-subtle">{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children, style }) => (
    <th
      style={style}
      className="px-3 py-2 text-left text-[11px] font-semibold text-ink uppercase tracking-wide whitespace-nowrap border-b border-border"
    >
      {children}
    </th>
  ),
  td: ({ children, style }) => <MdTableCell style={style}>{children}</MdTableCell>,

  img: ({ src, alt }) => (
    <img src={src} alt={alt ?? ''} className="max-w-full rounded-md border border-border my-3" />
  ),
};

/**
 * The same markdown, set for dense UI rather than for a document.
 *
 * Model text is markdown whether or not the surface showing it expects any: a quarter of the thoughts
 * recorded here carry `**bold**`, a fifth a bullet list, and a good number headings and numbered
 * steps. In the run console they were being printed literally — asterisks, hashes and all — which is
 * the one thing worse than not rendering them, since the reader is left decoding the marks in a
 * passage whose whole purpose is to be read.
 *
 * Merged over the defaults above rather than replacing them, so only the metrics differ and anything
 * unlisted (links, emphasis, images, tables' scroll wrapper) still renders exactly as it does in a KB
 * page. What changes is scale: the defaults are sized for a document with a page to itself, and
 * dropped into a 26rem scroller their `text-lg` headings and 24px rhythm turn six sentences into
 * something you have to scroll. Here a heading is a bold line, a paragraph is a paragraph, and the
 * space between them is the space between two lines of console.
 *
 * All six heading levels render alike on purpose. A thought is a few hundred words and the level a
 * model reaches for in one is arbitrary — `##` in this block and `###` in the next says nothing about
 * their relative rank, so honouring the difference would only make two identical things look unequal.
 */
export const compactMarkdownComponents: Components = {
  p: ({ children }) => (
    <p className="text-[12.5px] leading-relaxed text-ink-secondary break-words mb-2 last:mb-0">{children}</p>
  ),

  h1: ({ children }) => <CompactHeading>{children}</CompactHeading>,
  h2: ({ children }) => <CompactHeading>{children}</CompactHeading>,
  h3: ({ children }) => <CompactHeading>{children}</CompactHeading>,
  h4: ({ children }) => <CompactHeading>{children}</CompactHeading>,
  h5: ({ children }) => <CompactHeading>{children}</CompactHeading>,
  h6: ({ children }) => <CompactHeading>{children}</CompactHeading>,

  // `marker:` mutes the bullets and numbers themselves: at this size a column of full-strength discs
  // reads as louder than the sentences beside it.
  ul: ({ children }) => (
    <ul className="list-disc ml-4 mb-2 space-y-0.5 marker:text-ink-faint last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal ml-4 mb-2 space-y-0.5 marker:text-ink-faint last:mb-0">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-[12.5px] leading-relaxed text-ink-secondary break-words">{children}</li>
  ),

  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-border-strong pl-2.5 my-2 text-[12.5px] text-ink-muted italic">
      {children}
    </blockquote>
  ),

  hr: () => <hr className="my-2.5 border-border-subtle" />,

  pre: ({ children }) => (
    <pre className="bg-surface-subtle border border-border-subtle rounded-md p-2 mb-2 overflow-x-auto last:mb-0">
      {children}
    </pre>
  ),

  table: ({ children }) => (
    <div className="my-2 overflow-x-auto rounded-md border border-border">
      <table className="w-full text-[11px] border-collapse">{children}</table>
    </div>
  ),
  th: ({ children, style }) => (
    <th
      style={style}
      className="px-2 py-1 text-left text-[10px] font-semibold text-ink uppercase tracking-wide whitespace-nowrap border-b border-border"
    >
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="px-2 py-1 text-[11px] text-ink-secondary align-top">
      {children}
    </td>
  ),
};

const CompactHeading = ({ children }: { children?: ReactNode }) => (
  <p className="text-xs font-semibold text-ink mt-3 mb-1 first:mt-0">{children}</p>
);

/**
 * Renders markdown with GFM (tables, strikethrough, task lists, autolinks).
 *
 * Memoized because KB bodies are re-rendered on every parent state change — expanding a panel,
 * polling a run — and reparsing a long document each time is wasted work.
 */
export const Markdown = memo(function Markdown({
  children, className, components,
}: {
  children: string;
  className?: string;
  /** Merged over the defaults — CitedMarkdown uses this to inject citation markers. */
  components?: Components;
}) {
  return (
    <div className={cn('max-w-none', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components ? { ...markdownComponents, ...components } : markdownComponents}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
});

/** Flattens a rendered node's children back to plain text — what citation anchoring matches on. */
export function childrenToText(children: ReactNode): string {
  if (children === null || children === undefined || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(childrenToText).join('');
  if (typeof children === 'object' && 'props' in children) {
    return childrenToText((children as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}
